import embeddedData from '@/embedded/emoji-data';
/**
 * Unicode emoji generator for browser environments
 *
 * Provides methods to generate random emojis from embedded data.
 * Optimized for browser usage with immediate data loading and validation.
 */
export class UnicodeGenerator {
    emojiData = {};
    categories = [];
    /**
     * Load emoji data from embedded data
     *
     * Initializes the generator with embedded emoji dataset.
     * Validates data structure and ensures all categories are available.
     *
     * @throws {Error} When embedded data structure is invalid
     * @throws {Error} When no categories or emojis are found
     * @returns Promise that resolves when data is loaded
     */
    async loadData() {
        try {
            if (!embeddedData || typeof embeddedData !== 'object') {
                throw new Error('Invalid embedded data structure');
            }
            if (!embeddedData.data || typeof embeddedData.data !== 'object') {
                throw new Error('Missing or invalid data property in embedded data');
            }
            this.emojiData = embeddedData.data;
            this.categories = Object.keys(this.emojiData);
            if (this.categories.length === 0) {
                throw new Error('No categories found in embedded data');
            }
            const totalEmojis = Object.values(this.emojiData).reduce((sum, emojis) => sum + emojis.length, 0);
            if (totalEmojis === 0) {
                throw new Error('No emojis found in embedded data');
            }
            console.log(`📦 Loaded ${this.categories.length} categories (${totalEmojis} emojis) from embedded data`);
        }
        catch (error) {
            throw new Error(`Failed to load embedded emoji data: ${error}`);
        }
    }
    /**
     * Get all available categories
     *
     * Returns a list of all category names that can be used for filtering.
     * Categories are returned in the order they appear in the dataset.
     *
     * @throws {Error} When no categories are available (data not loaded)
     * @returns Array of category names
     */
    getCategories() {
        if (!this.categories || this.categories.length === 0) {
            throw new Error('No categories available. Make sure to call loadData() first.');
        }
        return [...this.categories];
    }
    /**
     * Get emojis by category
     *
     * Retrieves all emoji data for the specified category.
     * Returns a defensive copy to prevent external modification.
     *
     * @param category - The category name to retrieve emojis from
     * @throws {Error} When category is invalid or not found
     * @throws {Error} When no emoji data is available
     * @returns Array of emoji data objects for the category
     */
    getEmojisByCategory(category) {
        if (!category || typeof category !== 'string') {
            throw new Error('Category must be a non-empty string');
        }
        if (!this.emojiData || Object.keys(this.emojiData).length === 0) {
            throw new Error('No emoji data available. Make sure to call loadData() first.');
        }
        const emojis = this.emojiData[category];
        if (!emojis) {
            const availableCategories = Object.keys(this.emojiData).slice(0, 10).join(', ');
            throw new Error(`Category '${category}' not found. Available categories: ${availableCategories}...`);
        }
        return [...emojis];
    }
    /**
     * Validate input parameters
     *
     * Ensures all parameters meet requirements for safe emoji generation.
     * Validates count limits, type values, and category format.
     *
     * @param count - Number of emojis to generate
     * @param type - Output type specification
     * @param category - Optional category filter
     * @throws {Error} When any parameter is invalid
     */
    validateOptions(count, type, category) {
        if (typeof count !== 'number' || count < 1) {
            throw new Error('Count must be a positive number (minimum 1)');
        }
        if (count > 1000) {
            throw new Error('Count cannot exceed 1000 for performance reasons');
        }
        if (type && !['code', 'emoji', 'both'].includes(type)) {
            throw new Error('Type must be one of: "code", "emoji", or "both"');
        }
        if (category && (!category || typeof category !== 'string')) {
            throw new Error('Category must be a non-empty string');
        }
    }
    /**
     * Get available emojis based on category
     *
     * Returns either all emojis or emojis from a specific category.
     * Handles category validation and error reporting.
     *
     * @param category - Optional category to filter by
     * @throws {Error} When category is not found
     * @returns Array of available emoji data
     */
    getAvailableEmojis(category) {
        if (category) {
            if (!this.emojiData[category]) {
                const availableCategories = Object.keys(this.emojiData).slice(0, 10).join(', ');
                throw new Error(`Category '${category}' not found. Available categories: ${availableCategories}...`);
            }
            return this.emojiData[category];
        }
        return Object.values(this.emojiData).flat();
    }
    /**
     * Format result based on type and count
     *
     * Converts emoji data to the requested output format.
     * Handles single vs multiple results and different output types.
     *
     * @param randomEmojis - Array of randomly selected emoji data
     * @param count - Number of emojis requested
     * @param type - Output format type
     * @returns Formatted result as string, array, or emoji data object(s)
     */
    formatResult(randomEmojis, count, type) {
        if (count === 1) {
            const emoji = randomEmojis[0];
            switch (type) {
                case 'code':
                    return emoji.code;
                case 'emoji':
                    return emoji.emoji;
                case 'both':
                    return emoji;
                default:
                    return emoji.emoji;
            }
        }
        else {
            switch (type) {
                case 'code':
                    return randomEmojis.map(e => e.code);
                case 'emoji':
                    return randomEmojis.map(e => e.emoji);
                case 'both':
                    return randomEmojis;
                default:
                    return randomEmojis.map(e => e.emoji);
            }
        }
    }
    /**
     * Get random emoji(s) with flexible options
     *
     * Main method for generating random emojis with optional filtering.
     * Supports single or multiple emoji generation with various output formats.
     *
     * @param options - Configuration options for random generation
     * @throws {Error} When emoji data is not loaded
     * @throws {Error} When options are invalid
     * @throws {Error} When no emojis are available in specified category
     * @returns Random emoji(s) in the specified format
     */
    random(options) {
        if (!this.emojiData || Object.keys(this.emojiData).length === 0) {
            throw new Error('No emoji data available. Make sure to call loadData() first.');
        }
        const { count = 1, category, type = 'emoji' } = options || {};
        this.validateOptions(count, type, category);
        const availableEmojis = this.getAvailableEmojis(category);
        if (availableEmojis.length === 0) {
            throw new Error('No emojis available in the specified category');
        }
        const randomEmojis = [];
        for (let i = 0; i < count; i++) {
            const randomIndex = Math.floor(Math.random() * availableEmojis.length);
            randomEmojis.push(availableEmojis[randomIndex]);
        }
        return this.formatResult(randomEmojis, count, type);
    }
}
// Create default instance and load data immediately
const generator = new UnicodeGenerator();
generator.loadData().catch(error => {
    console.error('Failed to load Unicode-Gen data:', error);
});
// Export the instance as default
export default generator;
